import { AbstractControl, ValidationErrors } from "@angular/forms";
import moment from "moment-timezone";
import { VehicleType } from "src/app/models/agenda.model";

export function isColorBrigth(hexColor: string): boolean {
  // Elimina el # si está presente
  const hex = hexColor.replace('#', '');

  // Convierte a componentes R, G, B
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  // Calcula luminancia relativa (percepción visual humana)
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

  // Si la luminancia es alta, fondo es claro → texto negro, si no → texto blanco
  return luminance > 0.5;
}

export function validDate(control: AbstractControl): ValidationErrors | null {
  const value = control.value;
  if (!value) return null;

  return parseFlexibleDate(value) ? null : { invalidDate: true };
}


function parseFlexibleDate(value: unknown): Date | null {
  if (!value) return null;

  if (value instanceof Date) {
    return isNaN(value.getTime()) ? null : value;
  }

  if (typeof value !== 'string') {
    return null;
  }

  const trimmed = value.trim();
  if (!trimmed) return null;

  const isoDateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(trimmed);
  if (isoDateOnly) {
    const year = Number(isoDateOnly[1]);
    const month = Number(isoDateOnly[2]);
    const day = Number(isoDateOnly[3]);
    const date = new Date(year, month - 1, day);
    return isValidDateParts(date, year, month, day) ? date : null;
  }

  const dmy = /^(\d{2})[\/.\-](\d{2})[\/.\-](\d{4})$/.exec(trimmed);
  if (dmy) {
    const day = Number(dmy[1]);
    const month = Number(dmy[2]);
    const year = Number(dmy[3]);
    const date = new Date(year, month - 1, day);
    return isValidDateParts(date, year, month, day) ? date : null;
  }

  const ymdSlash = /^(\d{4})\/(\d{2})\/(\d{2})$/.exec(trimmed);
  if (ymdSlash) {
    const year = Number(ymdSlash[1]);
    const month = Number(ymdSlash[2]);
    const day = Number(ymdSlash[3]);
    const date = new Date(year, month - 1, day);
    return isValidDateParts(date, year, month, day) ? date : null;
  }

  const parsed = new Date(trimmed);
  return isNaN(parsed.getTime()) ? null : parsed;
}

function isValidDateParts(date: Date, year: number, month: number, day: number): boolean {
  return (
    !isNaN(date.getTime()) &&
    date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day
  );
}


export function minDateValidator(min: string) {
  const parsedMin = parseFlexibleDate(min);
  if (!parsedMin) {
    console.warn(`minDateValidator: la fecha mínima "${min}" no es válida.`);
    return () => null;
  }

  const minDate = parsedMin;

  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (!value) return null;

    const date = parseFlexibleDate(value);

    if (!date) {
      return { invalidDate: true };
    }

    return date < minDate ? { minDate: true } : null;
  };
}

export function getVehicleTypeLabel(vehicleType: VehicleType): string {
  const typeKeys: { [key in VehicleType]?: string } = {
    [VehicleType.MICRO]: 'vehicle.history_sidebar.vehicle_types.micro',
    [VehicleType.SEDAN]: 'vehicle.history_sidebar.vehicle_types.sedan',
    [VehicleType.CUV]: 'vehicle.history_sidebar.vehicle_types.cuv',
    [VehicleType.SUV]: 'vehicle.history_sidebar.vehicle_types.suv',
    [VehicleType.HATCHBACK]: 'vehicle.history_sidebar.vehicle_types.hatchback',
    [VehicleType.ROADSTER]: 'vehicle.history_sidebar.vehicle_types.roadster',
    [VehicleType.PICKUP]: 'vehicle.history_sidebar.vehicle_types.pickup',
    [VehicleType.VAN]: 'vehicle.history_sidebar.vehicle_types.van',
    [VehicleType.COUPE]: 'vehicle.history_sidebar.vehicle_types.coupe',
    [VehicleType.SUPERCAR]: 'vehicle.history_sidebar.vehicle_types.supercar',
    [VehicleType.CAMPERVAN]: 'vehicle.history_sidebar.vehicle_types.campervan',
    [VehicleType.MINITRUCK]: 'vehicle.history_sidebar.vehicle_types.minitruck',
    [VehicleType.CABRIOLET]: 'vehicle.history_sidebar.vehicle_types.cabriolet',
    [VehicleType.MINIVAN]: 'vehicle.history_sidebar.vehicle_types.minivan',
    [VehicleType.TRUCK]: 'vehicle.history_sidebar.vehicle_types.truck'
  };
  return typeKeys[vehicleType] || 'vehicle.history_sidebar.vehicle_types.unknown';
}

export const arrayToMapByKey = <
  T,
  K extends keyof T
>(items: T[], key: K): Map<T[K], T> =>
  new Map(items.map(item => [item[key], item]));

export function scrollToElementInContainerByIds(
  containerId: string,
  elementId: string,
  options?: {
    behavior?: ScrollBehavior;
    offset?: number;
  }
): void {
  if (!containerId || !elementId) return;

  const container = document.getElementById(containerId);
  const target = document.getElementById(elementId);

  if (!container || !target) return;

  if (!container.contains(target)) return;

  const containerRect = container.getBoundingClientRect();
  const targetRect = target.getBoundingClientRect();

  const offset = options?.offset ?? 0;

  const scrollTop =
    targetRect.top - containerRect.top + container.scrollTop - offset;

  container.scrollTo({
    top: scrollTop,
    behavior: options?.behavior ?? 'smooth',
  });
}

export function getEnumValues(enumObj: any): any[] {
  return Object.keys(enumObj)
    .filter(key => isNaN(Number(key)))
    .map(key => enumObj[key as keyof typeof enumObj]);
}
